// MUI imports for time picker
import {LocalizationProvider, TimePicker} from '@mui/x-date-pickers';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {DemoItem} from '@mui/x-date-pickers/internals/demo';

// Helper function to get nested value - backward compatible with flat and nested state
const getNestedValue = (values, fieldName) => {
  // Check if state has nested groups and use them if they exist
  if (values.patient && (fieldName.startsWith('patientId') || fieldName.startsWith('patientName') || fieldName.startsWith('age') || fieldName.startsWith('gender'))) {
    return values.patient[fieldName] || '';
  } else if (values.sampleDetails && (fieldName.includes('Collection') || fieldName.includes('Examination') || fieldName.includes('Analysis') || fieldName === 'dateOfReporting' || fieldName === 'modeOfCollection' || fieldName === 'abstinencePeriod' || fieldName === 'completeCollection' || fieldName === 'otherSite' || (fieldName === 'liquefactionTime' && values.sampleDetails.liquefactionTime !== undefined))) {
    return values.sampleDetails[fieldName] || '';
  } else if (values.macroscopic && (fieldName === 'volume' || fieldName === 'appearance' || fieldName === 'viscosity' || fieldName === 'pH' || fieldName === 'vialsFrozen' || fieldName === 'semenValidity' || fieldName === 'gobletColour' || fieldName === 'canisterNo' || fieldName === 'tankNo' || (fieldName === 'liquefactionTime' && values.sampleDetails && values.sampleDetails.liquefactionTime === undefined))) {
    return values.macroscopic[fieldName] || '';
  } else if (values.microscopic && (fieldName === 'spermConcentration' || fieldName === 'totalSpermCount' || fieldName === 'totalMotility' || fieldName === 'progressiveMotility' || fieldName === 'slowProgressiveMotility' || fieldName === 'nonProgressiveMotility' || fieldName === 'immotile' || fieldName === 'immotileSperm' || fieldName === 'fructose' || fieldName === 'totalMotileProgressive' || fieldName === 'morphologyNF' || fieldName === 'vitality' || fieldName === 'agglutination' || fieldName === 'aggregation' || fieldName === 'roundCells' || fieldName === 'otherCells' || fieldName === 'totalMotileSpermCount')) {
    return values.microscopic[fieldName] || '';
  } else if (values.morphology && (fieldName === 'normalForms' || fieldName === 'abnormalForms' || fieldName === 'headAbnormalities' || fieldName === 'midpieceAbnormalities' || fieldName === 'tailAbnormalities' || fieldName === 'cytoplasmicDroplets')) {
    return values.morphology[fieldName] || '';
  } else if (values.cryopreservation && (fieldName === 'vialsFrozen' || fieldName === 'strawsFrozen' || fieldName === 'semenValidity' || fieldName === 'cryoprotectantUsed')) {
    return values.cryopreservation[fieldName] || '';
  } else {
    // Fallback for flat state or unknown fields
    return values[fieldName] || '';
  }
};

const AnalysisTable = ({headers, rows, show, onChange, values, commonStyles}) => {
  // Check if we have exactly 3 headers to apply equal width
  const useEqualWidth = headers.length === 3;

  return (
    <div style={{marginBottom: '20px'}}>
      <table style={{width: '100%', borderCollapse: 'collapse', border: '1px solid #ddd'}}>
        <thead>
          <tr style={{backgroundColor: '#f0f0f0'}}>
            {headers.map((header, index) => (
              <th
                key={index}
                style={{
                  padding: '8px',
                  border: '1px solid #ddd',
                  textAlign: index === 0 ? 'left' : 'left', // First column left-aligned, others centered
                  width: useEqualWidth ? '33.33%' : 'auto', // Equal width for 3 columns
                  fontSize: '12px' // Smaller font for long headers
                }}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              <td style={{padding: '8px', border: '1px solid #ddd', fontWeight: 600}}>{row.label}</td>
              {headers[1] === 'PRE WASH' && (
                <td
                  style={{
                    padding: '8px',
                    border: '1px solid #ddd',
                    textAlign: show ? 'left' : 'left'
                  }}>
                  {row.namePre ? (
                    !show ? (
                      <div style={{display: 'flex', alignItems: 'center', justifyContent: 'flex-start', paddingLeft: '5px', gap: '5px'}}>
                        {row.typePre === 'select' ? (
                          <select name={row.namePre} value={getNestedValue(values, row.namePre)} onChange={onChange} style={{...commonStyles.input, width: row.width || '150px'}}>
                            {row.options.map(opt => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type={row.type || 'text'}
                            name={row.namePre}
                            value={getNestedValue(values, row.namePre)}
                            onChange={onChange}
                            placeholder={row.placeholder}
                            style={{...commonStyles.input, width: row.width || '150px'}}
                          />
                        )}
                        {row.unit && <span style={{marginLeft: '5px'}}>{row.unit}</span>}
                      </div>
                    ) : (
                      <span className='input' style={{fontSize: '14px', textAlign: 'left', display: 'block'}}>
                        {getNestedValue(values, row.namePre)}
                        {row.unit && ` ${row.unit}`}
                      </span>
                    )
                  ) : null}
                </td>
              )}
              <td
                style={{
                  padding: '8px',
                  border: '1px solid #ddd',
                  textAlign: show ? 'left' : 'left' // Dynamic alignment based on show prop
                }}>
                {!show ? (
                  <div style={{display: 'flex', alignItems: 'center', justifyContent: 'flex-start', paddingLeft: '5px', gap: '5px'}}>
                    {row.renderInput ? (
                      row.renderInput(values, onChange, commonStyles, show) === 'TIME_PICKER_COMPONENT' ? (
                        // Handle MUI TimePicker for time inputs
                        <div className={!show ? 'show' : 'hide'} style={{width: '100%'}}>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DemoItem>
                              <TimePicker
                                sx={{
                                  width: '180px',
                                  '& .MuiInputBase-root': {
                                    width: '180px',
                                    fontSize: '14px'
                                  }
                                }}
                                onChange={(e) => {
                                  let hours = e.$H,
                                    mins = e.$m,
                                    ampm = 'AM';
                                  let temMin = e.$m;
                                  temMin < 10 ? (mins = '0' + temMin) : (mins = temMin);
                                  if (e.$H >= 12) {
                                    if (e.$H === 12) {
                                      hours = 12;
                                      ampm = 'PM';
                                    } else {
                                      let temHour = e.$H - 12;
                                      temHour < 10 ? (hours = '0' + temHour) : (hours = temHour);
                                      ampm = 'PM';
                                    }
                                  } else if (e.$H === 0) {
                                    hours = 12;
                                    ampm = 'AM';
                                  } else {
                                    let temHour = e.$H;
                                    temHour < 10 ? (hours = '0' + temHour) : (hours = temHour);
                                    ampm = 'AM';
                                  }

                                  // Determine which group this belongs to
                                  let groupName = 'sampleDetails';
                                  const event = { target: { name: row.name, value: `${hours}-${mins}-${ampm}` } };
                                  onChange(event);
                                }}
                              />
                            </DemoItem>
                          </LocalizationProvider>
                        </div>
                      ) : (
                        row.renderInput(values, onChange, commonStyles, show) || <span>No input defined</span>
                      )
                    ) : (row.namePost || row.type) === 'select' ? (
                      <select name={row.namePost || row.name} value={getNestedValue(values, row.namePost || row.name)} onChange={onChange} style={{...commonStyles.input, width: row.width || '150px'}}>
                        {row.options.map(opt => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={row.type || 'text'}
                        name={row.namePost || row.name}
                        value={getNestedValue(values, row.namePost || row.name)}
                        onChange={onChange}
                        placeholder={row.placeholder}
                        style={{...commonStyles.input, width: row.width || '150px'}}
                      />
                    )}
                    {row.unit && <span style={{marginLeft: '5px'}}>{row.unit}</span>}
                  </div>
                ) : (
                  <span className='input' style={{fontSize: '14px', textAlign: 'left', display: 'block'}}>
                    {row.renderInput ? (
                      row.renderInput(values, onChange, commonStyles, show)
                    ) : (
                      <>
                        {getNestedValue(values, row.namePost || row.name)}
                        {row.unit && ` ${row.unit}`}
                      </>
                    )}
                  </span>
                )}
              </td>
              {headers.length > 2 && <td style={{padding: '8px', border: '1px solid #ddd', textAlign: 'left'}}>{row.reference}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default AnalysisTable;
